import { useState, useMemo } from 'react';
import { SaleOffer, BuyRequest } from '../models/Marketplace';
import { User } from '../models/User';
import { getAllUsers } from '../data/userDatabase.ts';
import { 
  getMatches,
  handleContactPerson,
  ContactHandler,
  canUserContactForOffer,
  canUserContactForRequest
} from '../utils/marketplaceUtils';

export interface ContactModalState {
  isOpen: boolean;
  type: 'buyers' | 'sellers';
  sourceItem: SaleOffer | BuyRequest | null;
}

export interface UseContactModalReturn {
  // State
  contactModal: ContactModalState;
  selectedBuyer: User | null;
  selectedSeller: User | null;
  relatedEntityId: string | undefined;
  showMessageComposer: boolean;
  
  // Actions
  openContactBuyers: (offer: SaleOffer) => void;
  openContactSellers: (request: BuyRequest) => void;
  closeContactModal: () => void;
  handleContactPerson: (name: string, entityId: string) => void;
  setShowMessageComposer: (show: boolean) => void;
  resetContactState: () => void;
  
  // Computed values
  matches: (SaleOffer | BuyRequest)[];
  canShowContactBuyers: (offer: SaleOffer, currentUser: User, highestBuyRequest: number) => boolean;
  canShowContactSellers: (request: BuyRequest, currentUser: User, lowestSalesOffer: number) => boolean;
}

export const useContactModal = (
  salesOffers: SaleOffer[],
  buyRequests: BuyRequest[],
  onNavigate?: (page: string) => void
): UseContactModalReturn => {
  const [contactModal, setContactModal] = useState<ContactModalState>({
    isOpen: false,
    type: 'buyers',
    sourceItem: null
  });
  
  const [selectedBuyer, setSelectedBuyer] = useState<User | null>(null);
  const [selectedSeller, setSelectedSeller] = useState<User | null>(null);
  const [relatedEntityId, setRelatedEntityId] = useState<string | undefined>(undefined);
  const [showMessageComposer, setShowMessageComposer] = useState(false);

  // Calculate matches based on current modal state
  const matches = useMemo(() => {
    if (!contactModal.sourceItem) return [];
    
    const targetItems = contactModal.type === 'buyers' ? buyRequests : salesOffers;
    return getMatches(contactModal.sourceItem, targetItems, contactModal.type);
  }, [contactModal.sourceItem, contactModal.type, salesOffers, buyRequests]);

  // Actions
  const openContactBuyers = (offer: SaleOffer) => {
    setContactModal({
      isOpen: true,
      type: 'buyers',
      sourceItem: offer
    });
  };

  const openContactSellers = (request: BuyRequest) => {
    setContactModal({
      isOpen: true,
      type: 'sellers',
      sourceItem: request
    });
  };

  const closeContactModal = () => {
    setContactModal({
      isOpen: false,
      type: 'buyers',
      sourceItem: null
    });
  };

  const handleContactPersonLocal = async (name: string, entityId: string) => {
    const contactHandler: ContactHandler = {
      onSetBuyer: setSelectedBuyer,
      onSetSeller: setSelectedSeller,
      onSetRelatedEntity: setRelatedEntityId,
      onShowMessageComposer: setShowMessageComposer,
      onNavigate,
      getAllUsers
    };
    
    await handleContactPerson(
      name,
      entityId,
      contactModal.type === 'buyers' ? 'buyer' : 'seller',
      contactHandler
    );
    
    closeContactModal();
  };

  const resetContactState = () => {
    setSelectedBuyer(null);
    setSelectedSeller(null);
    setRelatedEntityId(undefined);
    setShowMessageComposer(false);
    closeContactModal();
  };

  // Computed helper functions
  const canShowContactBuyers = (offer: SaleOffer, currentUser: User, highestBuyRequest: number) => {
    return parseFloat(offer.sharePrice) <= highestBuyRequest && 
           canUserContactForOffer(offer, currentUser);
  };

  const canShowContactSellers = (request: BuyRequest, currentUser: User, lowestSalesOffer: number) => {
    return parseFloat(request.sharePrice) >= lowestSalesOffer && 
           canUserContactForRequest(request, currentUser);
  };

  return {
    // State
    contactModal,
    selectedBuyer,
    selectedSeller,
    relatedEntityId,
    showMessageComposer,
    
    // Actions
    openContactBuyers,
    openContactSellers,
    closeContactModal,
    handleContactPerson: handleContactPersonLocal,
    setShowMessageComposer,
    resetContactState,
    
    // Computed values
    matches,
    canShowContactBuyers,
    canShowContactSellers
  };
}; 